import React from 'react';
import { Config } from './types';
import { StyledPanel, StyledPanelTitle } from '../Text/TextWidgetControl';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import {
  Padding,
  PaddingData,
} from 'builder/components/WidgetConfigurations/Padding';
import { Panel, Label } from '@uhg-harmony/uitk-react';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from 'builder/components/WidgetSizeLocation';
import {
  Background,
  BackgroundData,
} from 'builder/components/WidgetConfigurations/Background/Background';
import { Typography } from 'builder/components/WidgetConfigurations/Typography/Typography';
import { Icon } from 'shared/theme/link-ui-react/Icon';

const ArrowIcon = (
  <Icon
    icon="ArrowRightOpen"
    color="#000000"
    width="0.8125em"
    height="0.9375em"
  />
);

const NotificationWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const {
    value,
    onChange,
    widgetState,
    pages,
    currentPage: { layoutLocked },
  } = props;

  const updatePaddingData = (data: PaddingData) => {
    onChange({
      ...value,
      paddingData: data,
    });
  };

  const updateBorderData = (data: BorderData) => {
    onChange({
      ...value,
      borderData: data,
    });
  };
  const handleLineColor = data => {
    onChange({
      ...value,
      lineColor: data,
    });
  };

  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const updateBackgroundData = (data: BackgroundData) => {
    onChange({
      ...value,
      backgroundData: data,
    });
  };
  const handleBackgroundColor = (data: string) => {
    onChange({
      ...value,
      backgroundData: {
        ...value.backgroundData,
        color: data,
      },
    });
  };
  const handleFontData = data => {
    onChange({
      ...value,
      font: data,
    });
  };
  const handleFontSizeData = data => {
    onChange({
      ...value,
      fontSize: data,
    });
  };
  const handleBoldData = data => {
    onChange({
      ...value,
      bold: data,
    });
  };
  const handleItalicData = data => {
    onChange({
      ...value,
      italic: data,
    });
  };
  const handleUnderlineData = data => {
    onChange({
      ...value,
      underline: data,
    });
  };
  const handleStrikethroughData = data => {
    onChange({
      ...value,
      strikeThrough: data,
    });
  };
  const handleLineSpacingData = data => {
    onChange({
      ...value,
      lineSpacing: data,
    });
  };
  const handleCharacterSpacingData = data => {
    onChange({
      ...value,
      characterSpacing: data,
    });
  };
  const handleTextColorData = data => {
    onChange({
      ...value,
      textColor: data,
    });
  };

  return (
    <>
      <Panel.Group>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Typography</Label>}
            initiallyClosed
            icon={ArrowIcon}
            data-test-id="typography-panel"
          >
            <Typography
              key={'typography-component'}
              data-test-id="typography-component"
              widgetState={widgetState}
              boldData={handleBoldData}
              handleFontSize={handleFontSizeData}
              handleFont={handleFontData}
              handleItalicData={handleItalicData}
              handleUnderLine={handleUnderlineData}
              handleStrikeThrough={handleStrikethroughData}
              handleLineSpacing={handleLineSpacingData}
              handleCharacterSpacing={handleCharacterSpacingData}
              handleTextColor={handleTextColorData}
              hideAlign={true}
              hideTextHighlight={true}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
              pages={pages}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            key={'size-location-panel'}
            title={<Label style={StyledPanelTitle}>Size and Location</Label>}
            initiallyClosed
            icon={ArrowIcon}
            data-test-id="size-location-panel"
          >
            <WidgetSizeLocation
              data-test-id="widget-size-location"
              sizeLocation={{
                xLocation: value.xLocation,
                yLocation: value.yLocation,
                width: value.width,
                height: value.height,
                uniqueId: value.uniqueId,
              }}
              updateWidgetSizeLocation={updateWidgetSizeLocation}
              activeWidgetId={widgetState.id}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            key={'background-panel'}
            title={<Label style={StyledPanelTitle}>Background</Label>}
            initiallyClosed
            icon={ArrowIcon}
            data-test-id="background-panel"
          >
            <Background
              data-test-id="background-data"
              disabled={layoutLocked}
              backgroundData={updateBackgroundData}
              widgetState={widgetState}
              handleBackgroundColor={handleBackgroundColor}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            key={'hover-border-panel'}
            title={<Label style={StyledPanelTitle}>Border</Label>}
            initiallyClosed
            icon={ArrowIcon}
            data-test-id="hover-border-panel"
          >
            <Border
              data-test-id="hover-border-data"
              updateBorderData={updateBorderData}
              handleLineColor={handleLineColor}
              widgetState={widgetState}
              pages={props.pages}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            key={'hover-padding-panel'}
            title={<Label style={StyledPanelTitle}>Padding</Label>}
            initiallyClosed
            icon={ArrowIcon}
            data-test-id="padding-panel"
          >
            <Padding
              paddingData={updatePaddingData}
              widgetState={widgetState}
            />
          </Panel>
        </StyledPanel>
      </Panel.Group>
    </>
  );
};

export default NotificationWidgetControl;
